import clsx from 'clsx';
import React from 'react';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  size?: 'lg' | 'sm';
  label?: string;
  labelOptional?: boolean;
  assistiveText?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  prefix?: string;
  suffix?: string;
  containerClassName?: string;
  inputClassName?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      size = 'lg',
      label,
      labelOptional,
      assistiveText,
      error,
      leftIcon,
      rightIcon,
      prefix,
      suffix,
      containerClassName,
      inputClassName,
      disabled,
      id,
      ...rest
    },
    ref,
  ) => {
    const isLg = size === 'lg';
    const hasError = !!error;

    return (
      <div className={clsx('flex flex-col gap-xs', containerClassName)}>
        {label && (
          <div className="flex items-center justify-between gap-sm">
            <label htmlFor={id} className="text-label-small text-content-dark">
              {label}
            </label>
            {labelOptional && (
              <span className="text-label-small text-content-light">(Optional)</span>
            )}
          </div>
        )}

        <div
          className={clsx(
            'flex items-center border border-solid overflow-hidden',
            isLg ? 'gap-sm p-sm rounded-radius-md' : 'gap-sm px-sm py-xs rounded-radius-sm',
            disabled
              ? 'bg-surface-lighter border-stroke-light cursor-not-allowed'
              : hasError
                ? 'bg-surface-white border-danger-default'
                : 'bg-surface-white border-stroke-light hover:border-stroke-dark focus-within:border-stroke-interactive',
          )}
        >
          {leftIcon && (
            <span className={clsx('shrink-0 flex items-center text-content-dark', isLg ? 'size-5' : 'size-4')}>
              {leftIcon}
            </span>
          )}
          {prefix && (
            <span
              className={clsx(
                'shrink-0 text-content-dark',
                isLg ? 'text-body-large' : 'text-body-small',
                disabled && 'opacity-50',
              )}
            >
              {prefix}
            </span>
          )}
          <input
            ref={ref}
            id={id}
            disabled={disabled}
            className={clsx(
              'flex-1 min-w-0 bg-transparent outline-none',
              isLg ? 'text-body-large' : 'text-body-small',
              'text-content-dark placeholder:text-content-light placeholder:font-normal',
              disabled && 'cursor-not-allowed opacity-50',
              inputClassName,
            )}
            autoComplete="off"
            aria-autocomplete="none"
            spellCheck={false}
            {...rest}
          />
          {suffix && (
            <span
              className={clsx(
                'shrink-0 text-content-dark',
                isLg ? 'text-body-large' : 'text-body-small',
                disabled && 'opacity-50',
              )}
            >
              {suffix}
            </span>
          )}
          {rightIcon && (
            <span className={clsx('shrink-0 flex items-center text-content-dark', isLg ? 'size-5' : 'size-4')}>
              {rightIcon}
            </span>
          )}
        </div>

        {(hasError || assistiveText) && (
          <p className={clsx('text-body-small', hasError ? 'text-danger-dark' : 'text-content-light')}>
            {hasError ? error : assistiveText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'V2Input';

export { Input };
export type { InputProps as V2InputProps };
